/**
 * This middleware looks for actions that affect the time-on-task tracking for time spent on research materials
 */
import * as c from '../constants/research';
import * as pages from '../constants/pages';
import { APP_SET_PAGE, APP_RESET } from '../constants/app';
import Timer from '../utils/timer';
import { apiAction } from '../actions/api';

// number of seconds between recording timer values, 0 to disable recording
const recordInterval = 20;

// actions that we want to pay attention to
const triggerActions = [
  APP_SET_PAGE,
  APP_RESET,
  c.RESEARCH_SET_PAGE,
  c.RESEARCH_OPEN_HELP,
  c.RESEARCH_CLOSE_HELP,
  c.RESEARCH_SAVE_ACTIVITY_FAILURE,
];

// pages that are considered 'research pages'
const researchPages = [pages.PAGE_RESEARCH, pages.PAGE_BUILD];

// a collection of timers
let timers = {};

// a collection of visits
let visits = {};

const getTimer = (name) => {
  if (!timers[name]) {
    timers[name] = new Timer(name);
  }
  return timers[name];
};

const visit = (code) => {
  if (!visits[code]) {
    visits[code] = 1;
  } else {
    visits[code]++;
  }
};

const timeOnTaskLogger = ({ dispatch, getState }) => (next) => {
  let interval;
  // record activity at the specified interval.  note we are passing the dispatch call to the record function
  if (recordInterval > 0) {
    interval = setInterval(() => record(dispatch), recordInterval * 1000);
  }

  return (action) => {
    // skip if none of the trigger actions have taken place
    if (triggerActions.indexOf(action.type) < 0) {
      return next(action);
    }

    const { selectedPage, selectedHelp } = getState().research;

    switch (action.type) {
      case APP_RESET:
        if (interval) {
          clearInterval(interval);
        }
        break;
      case APP_SET_PAGE:
        if (researchPages.indexOf(action.page) < 0) {
          // pause timer for page researchState.selectedPage
          getTimer(selectedPage).pause();
        } else if (selectedPage.length) {
          // resume timer for page researchState.selectedPage
          getTimer(selectedPage).start();
          visit(selectedPage);
        }
        break;
      case c.RESEARCH_SET_PAGE:
        // pause timer for page researchState.selectedPage
        getTimer(selectedPage).pause();
        // resume timer for page action.page
        getTimer(action.page.code).resume();
        visit(action.page.code);
        break;
      case c.RESEARCH_OPEN_HELP:
        // pause timer for help researchState.selectedHelp
        getTimer(selectedHelp).pause();
        // start timer for help action.code
        getTimer(action.code).resume();
        visit(action.code);
        break;
      case c.RESEARCH_CLOSE_HELP:
        // pause timer for help researchState.selectedHelp
        getTimer(selectedHelp).pause();
        break;
      case c.RESEARCH_SAVE_ACTIVITY_FAILURE:
        // TODO: merge the visits and time back into the current accumulators so we can try them again later
        console.error('could not save activity :(');
        break;
      default:
        // no-op
        break;
    }

    return next(action);
  };
};

const record = (dispatch) => {
  const timerData = Object.values(timers).reduce((acc, timer) => {
    const elapsed = timer.getElapsed();
    if (elapsed && timer.name) {
      acc[timer.name] = elapsed;
      timer.reset();
    }
    return acc;
  }, {});

  // collect and reset visits
  const visitData = visits;
  visits = {};

  // don't bother sending data if nothing happened
  if (
    Object.keys(timerData).length === 0 &&
    Object.keys(visitData).length === 0
  ) {
    return;
  }

  dispatch(
    apiAction(
      [
        c.RESEARCH_SAVE_ACTIVITY,
        c.RESEARCH_SAVE_ACTIVITY_SUCCESS,
        c.RESEARCH_SAVE_ACTIVITY_FAILURE,
      ],
      (api) => api.saveActivity(timerData, visitData),
      {
        timerData,
        visitData,
      }
    )
  );
};

// debug
window.showTimers = () => {
  console.log(timers);
  for (let key in timers) {
    timers[key].debug();
  }
};

export default timeOnTaskLogger;
